import React, { useEffect, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { apiService, ConversationCart, OtherNumberCart } from '../services/api';
import type { AiCommerceLine } from '../services/ai.types';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { colors, spacing, typography, withAlpha } from '../theme';
import { designTokens } from '../theme/design-system';
import { AiBotIcon } from './icons/AppIcon';

/**
 * Conversation → Cart: the conversation's shared cart — the same one the AI fills, the customer
 * edits in the browser pages and the team manages here — priced with the business's taxes and
 * charges. Change quantities, remove items, turn it into an order or quotation, or clear it.
 * While it is empty, the customer's open cart on another number can be continued here.
 * Reloads when `refreshKey` changes (a new message) and every 20 s while open.
 */

const money = (amount: number, currency: string) => `${Math.round(amount * 100) / 100}${currency ? ` ${currency}` : ''}`;
const errorOf = (res: any, fallback: string) => res?.errors?.[0]?.message || res?.errors?.[0] || fallback;

export function OpenCartsCard({ conversationId, refreshKey, canOrder, canQuote, canEdit, onCreate }: {
  conversationId: string;
  refreshKey?: unknown;
  canOrder: boolean;
  canQuote: boolean;
  canEdit: boolean;
  onCreate: (type: 'order' | 'quotation', lines: AiCommerceLine[]) => void;
}) {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const { showNotification } = useAppContext();
  const [cart, setCart] = useState<ConversationCart | null>(null);
  const [other, setOther] = useState<OtherNumberCart | undefined>();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const saving = useRef(false);

  useEffect(() => {
    if (!conversationId) return;
    let alive = true;
    const load = () => {
      if (saving.current) return;
      apiService.getConversationCarts(conversationId).then((res) => {
        if (!alive || res.statusCode !== 1000 || !res.data) return;
        setCart(res.data.cart);
        setOther(res.data.otherCart);
      }).catch(() => undefined);
    };
    // Shortly after a message, so the AI's reply to it has updated the cart.
    const first = setTimeout(load, refreshKey === undefined ? 0 : 1500);
    const every = setInterval(load, 20000);
    return () => { alive = false; clearTimeout(first); clearInterval(every); };
  }, [conversationId, refreshKey, reload]);

  useEffect(() => { setCart(null); setOther(undefined); setOpen(false); }, [conversationId]);

  const run = async (key: string, action: () => Promise<any>) => {
    setBusy(key);
    saving.current = true;
    try {
      const res = await action();
      if (res.statusCode !== 1000) throw new Error(errorOf(res, t('ai.actionFailed')));
      if (res.data?.cart) setCart(res.data.cart);
      setReload((r) => r + 1);
    } catch (error: any) {
      showNotification('error', t('common.error'), error?.message || t('ai.actionFailed'));
    } finally {
      saving.current = false;
      setBusy(null);
    }
  };

  const lines = cart?.lines || [];
  const setQuantity = (index: number, quantity: number) => run(`q${index}`, () => apiService.setConversationCart(conversationId,
    lines.map((l, i) => ({ productId: l.productId, ...(l.variantId ? { variantId: l.variantId } : {}), quantity: i === index ? quantity : l.quantity }))
      .filter((l) => l.quantity > 0)));

  if (!lines.length && !other) return null;

  // The customer's cart on another number, while this conversation's is empty.
  if (!lines.length && other) {
    return (
      <View style={styles.box}>
        <View style={[styles.summary, { flexDirection: rtl.rowDirection }]}>
          <Ionicons name="swap-horizontal-outline" size={18} color={colors.primary as string} />
          <Text style={[styles.summaryText, { textAlign: rtl.textStart }]} numberOfLines={2}>
            {t('conversation.cart.otherNumber', { count: other.items, total: money(other.total, other.currency) })}
          </Text>
        </View>
        {canEdit && (
          <View style={[styles.actions, styles.pad, { flexDirection: rtl.rowDirection }]}>
            <TouchableOpacity style={[styles.button, styles.primary]} disabled={!!busy} onPress={() => run('continue', () => apiService.decideConversationCart(conversationId, other.conversationId, 'continue'))}>
              {busy === 'continue' ? <ActivityIndicator size="small" color="white" /> : <Ionicons name="arrow-down-circle-outline" size={14} color="white" />}
              <Text style={[styles.buttonText, { color: 'white' }]}>{t('conversation.cart.continueHere')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.button} disabled={!!busy} onPress={() => run('new', () => apiService.decideConversationCart(conversationId, other.conversationId, 'new'))}>
              <Text style={styles.buttonText}>{t('conversation.cart.startNew')}</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    );
  }

  const c = cart!;
  const commerceLines: AiCommerceLine[] = lines.map((l) => ({ productId: l.productId, ...(l.variantId ? { variantId: l.variantId } : {}), quantity: l.quantity }));
  const by = c.updatedBy === 'customer' ? t('conversation.cart.byCustomer') : c.updatedBy === 'ai' ? (c.agent?.name ? t('conversation.cart.fromAgent', { name: c.agent.name }) : t('conversation.cart.fromAssistant')) : t('conversation.cart.byTeam');

  return (
    <View style={styles.box}>
      <TouchableOpacity style={[styles.summary, { flexDirection: rtl.rowDirection }]} onPress={() => setOpen((o) => !o)} accessibilityRole="button">
        <Ionicons name="cart-outline" size={18} color={colors.primary as string} />
        <Text style={[styles.summaryText, { textAlign: rtl.textStart }]} numberOfLines={1}>
          {t('conversation.cart.summary', { count: lines.length, total: money(c.total, c.currency) })}
        </Text>
        <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary as string} />
      </TouchableOpacity>
      {open && (
        <View style={styles.cart}>
          <View style={[styles.sourceRow, { flexDirection: rtl.rowDirection }]}>
            {c.updatedBy === 'ai' && <AiBotIcon size={12} color={colors.text.secondary as string} />}
            <Text style={[styles.source, { textAlign: rtl.textStart }]}>
              {by}{c.updatedAt ? ` · ${new Date(c.updatedAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' } as any)}` : ''}
            </Text>
          </View>
          {lines.map((line, index) => (
            <View key={`${line.productId}:${line.variantId || ''}`} style={[styles.line, { flexDirection: rtl.rowDirection }]}>
              <Text style={[styles.lineName, { textAlign: rtl.textStart }]} numberOfLines={2}>{line.name}</Text>
              {canEdit ? (
                <View style={[styles.stepper, { flexDirection: rtl.rowDirection }]}>
                  <TouchableOpacity disabled={!!busy} onPress={() => setQuantity(index, line.quantity - 1)} accessibilityLabel={t('conversation.cart.less')}>
                    <Ionicons name={line.quantity > 1 ? 'remove-circle-outline' : 'trash-outline'} size={18} color={(line.quantity > 1 ? colors.primary : colors.error) as string} />
                  </TouchableOpacity>
                  {busy === `q${index}` ? <ActivityIndicator size="small" color={colors.primary as string} /> : <Text style={styles.qty}>{line.quantity}</Text>}
                  <TouchableOpacity disabled={!!busy} onPress={() => setQuantity(index, line.quantity + 1)} accessibilityLabel={t('conversation.cart.more')}>
                    <Ionicons name="add-circle-outline" size={18} color={colors.primary as string} />
                  </TouchableOpacity>
                </View>
              ) : <Text style={styles.qty}>× {line.quantity}</Text>}
              <Text style={styles.lineTotal}>{money(line.total, c.currency)}</Text>
            </View>
          ))}
          {(c.taxes.length > 0 || c.charges.length > 0) && (
            <View style={[styles.line, styles.divider, { flexDirection: rtl.rowDirection }]}>
              <Text style={[styles.extra, { textAlign: rtl.textStart }]}>{t('conversation.cart.subtotal')}</Text>
              <Text style={styles.extra}>{money(c.subtotal, c.currency)}</Text>
            </View>
          )}
          {c.taxes.map((x) => (
            <View key={`t-${x.name}`} style={[styles.line, { flexDirection: rtl.rowDirection }]}>
              <Text style={[styles.extra, { textAlign: rtl.textStart }]}>{x.name} ({x.rate}%)</Text>
              <Text style={styles.extra}>{money(x.amount, c.currency)}</Text>
            </View>
          ))}
          {c.charges.map((x) => (
            <View key={`c-${x.name}`} style={[styles.line, { flexDirection: rtl.rowDirection }]}>
              <Text style={[styles.extra, { textAlign: rtl.textStart }]}>{x.name}</Text>
              <Text style={styles.extra}>{money(x.amount, c.currency)}</Text>
            </View>
          ))}
          <View style={[styles.line, styles.divider, { flexDirection: rtl.rowDirection }]}>
            <Text style={[styles.totalText, { textAlign: rtl.textStart }]}>{t('conversation.cart.total')}</Text>
            <Text style={styles.totalText}>{money(c.total, c.currency)}</Text>
          </View>
          <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
            {canOrder && (
              <TouchableOpacity style={[styles.button, styles.primary]} onPress={() => onCreate('order', commerceLines)}>
                <Ionicons name="bag-check-outline" size={14} color="white" />
                <Text style={[styles.buttonText, { color: 'white' }]}>{t('conversation.cart.createOrder')}</Text>
              </TouchableOpacity>
            )}
            {canQuote && (
              <TouchableOpacity style={styles.button} onPress={() => onCreate('quotation', commerceLines)}>
                <Ionicons name="document-text-outline" size={14} color={colors.primary as string} />
                <Text style={styles.buttonText}>{t('conversation.cart.createQuotation')}</Text>
              </TouchableOpacity>
            )}
            {canEdit && (
              <TouchableOpacity style={styles.button} disabled={busy === 'clear'} onPress={() => run('clear', () => apiService.clearConversationCart(conversationId))}>
                {busy === 'clear' ? <ActivityIndicator size="small" color={colors.error as string} /> : <Ionicons name="trash-outline" size={14} color={colors.error as string} />}
                <Text style={[styles.buttonText, { color: colors.error as string }]}>{t('conversation.cart.clear')}</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      )}
    </View>
  );
}

const getStyles = () => StyleSheet.create({
  box: { marginHorizontal: spacing.sm, marginTop: spacing.xs, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: withAlpha(colors.primary as string, 0.35), backgroundColor: withAlpha(colors.primary as string, 0.05) },
  summary: { alignItems: 'center', gap: spacing.xs, paddingHorizontal: spacing.sm, paddingVertical: 8 },
  summaryText: { flex: 1, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  pad: { paddingHorizontal: spacing.sm, paddingBottom: spacing.sm },
  cart: { paddingHorizontal: spacing.sm, paddingBottom: spacing.sm, gap: 4, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.border },
  sourceRow: { alignItems: 'center', gap: 4, marginTop: spacing.xs },
  source: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
  line: { alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
  lineName: { flex: 1, fontSize: typography.fontSize.sm, color: colors.text.primary },
  stepper: { alignItems: 'center', gap: 6 },
  qty: { minWidth: 22, textAlign: 'center', fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  lineTotal: { minWidth: 70, textAlign: 'right', fontSize: typography.fontSize.sm, color: colors.text.primary },
  divider: { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.border, paddingTop: 4 },
  extra: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
  totalText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  actions: { flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs },
  button: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: spacing.sm, paddingVertical: 6, borderRadius: designTokens.borderRadius.sm, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  primary: { backgroundColor: colors.primary, borderColor: colors.primary },
  buttonText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.primary },
});
